import { Link } from '@tanstack/react-router';
import { Maximize2, MessageSquarePlus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { lastConversation } from './assistant-launcher';
import { ChatView } from './chat-view';
import { usePageContext } from './page-context';

/* ---------------------------------------------------------------------------
   העוזר במרכז דף הבית — המקום הראשון שהעין נוחתת עליו אחרי הכניסה, ולא כפתור
   צף בפינה. אותה שיחה כמו במגירה ובמסך המלא (השיחה האחרונה נזכרת), כך שאפשר
   להתחיל כאן ולהמשיך בכל מסך אחר. בדף הבית הכפתור הצף מוסתר.
   --------------------------------------------------------------------------- */

export function HomeAssistant() {
  const { t } = useTranslation('assistant');
  const { session } = useAuth();
  const context = usePageContext();
  const [conversationId, setConversationId] = useState<string | null>(() => lastConversation.get());

  const choose = (id: string | null) => {
    setConversationId(id);
    lastConversation.set(id);
  };

  const firstName = session?.user.name.trim().split(/\s+/)[0] ?? '';

  return (
    <section
      aria-labelledby="home-assistant-title"
      className="flex h-[min(68dvh,38rem)] min-h-[24rem] flex-col overflow-hidden rounded-(--radius-xl) border border-accent-border/60 bg-surface shadow-md"
    >
      <header className="flex items-start gap-3 border-b border-border bg-gradient-to-b from-accent-subtle/60 to-surface px-5 pt-5 pb-4">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent text-fg-on-accent shadow-sm">
          <Sparkles className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 id="home-assistant-title" className="text-lg font-semibold text-fg">
            {firstName ? t('home.greeting', { name: firstName }) : t('home.greetingNoName')}
          </h2>
          <p className="mt-0.5 text-sm text-fg-muted">{t('home.subtitle')}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {conversationId ? (
            <Button
              variant="ghost"
              size="icon"
              className="size-9"
              aria-label={t('launcher.newChat')}
              title={t('launcher.newChat')}
              onClick={() => choose(null)}
            >
              <MessageSquarePlus aria-hidden />
            </Button>
          ) : null}
          <Button variant="ghost" size="icon" className="size-9" asChild>
            <Link
              to="/assistant"
              search={conversationId ? { c: conversationId } : {}}
              aria-label={t('launcher.fullPage')}
              title={t('launcher.fullPage')}
            >
              <Maximize2 aria-hidden />
            </Link>
          </Button>
        </div>
      </header>
      <ChatView
        variant="hero"
        conversationId={conversationId}
        onConversation={(c) => choose(c.id)}
        onMissing={() => choose(null)}
        context={context}
      />
    </section>
  );
}
